import { useEffect, useMemo, useSyncExternalStore } from 'react';
import { useAuth } from '../auth/AuthContext';
import { useGlobalRealtime } from '../realtime/GlobalRealtimeProvider';
import { buildRelationIndex, pendingRequests, type RelationIndex } from './contactsModel';
import {
  bindContactsOwner,
  getContactsState,
  refreshContacts,
  setContactPresence,
  subscribeContacts,
  type ContactsState,
} from './contactsStore';

export type ContactsView = ContactsState & {
  /** Входящие висящие заявки без тех, что ждут «Отменить» после отклонения. */
  incoming: ContactsState['requests'];
  outgoing: ContactsState['requests'];
  relation: RelationIndex;
};

/**
 * Контакты и заявки для экрана: общее состояние (`contactsStore`) плюс живые обновления.
 *
 * <p>Сокет присылает «контакты изменились» (кто-то принял заявку, прислал новую) и события
 * «в сети» — оба подхватываются здесь, чтобы ни одному экрану не приходилось подписываться
 * самому. Перечитывать при возврате на экран — забота экрана (`useFocusEffect`): хуку не
 * видно, какой экран сейчас на виду.
 */
export function useContacts(): ContactsView {
  const { user } = useAuth();
  const { subscribeContactsChanged, subscribePresence } = useGlobalRealtime();
  const ownerId = user?.userId ?? null;
  // Привязка к владельцу — до первого чтения: иначе первый кадр нового аккаунта показал бы
  // контакты прошлого. Вызов идемпотентен, повторные рендеры его не замечают.
  bindContactsOwner(ownerId);
  const state = useSyncExternalStore(subscribeContacts, getContactsState, getContactsState);

  useEffect(() => subscribeContactsChanged(() => void refreshContacts()), [subscribeContactsChanged]);
  useEffect(
    () =>
      subscribePresence((event) => {
        if (event.userId) setContactPresence(event.userId, event.online);
      }),
    [subscribePresence],
  );

  return useMemo(() => {
    const hidden = new Set(state.hiddenRequestIds);
    const visibleRequests = state.requests.filter((r) => !hidden.has(r.id));
    const { incoming, outgoing } = pendingRequests(visibleRequests);
    return {
      ...state,
      incoming,
      outgoing,
      relation: buildRelationIndex(ownerId, state.contacts, visibleRequests),
    };
  }, [ownerId, state]);
}
